import { SaiQuickTourPlacement } from 'app/ui/sai-quick-tour/sai-quick-tour.types';

/** Matches Fuse `md` — sidebar switches to overlay at this width. */
export const SAI_QUICK_TOUR_MOBILE_MAX_WIDTH = 959;

const DEFAULT_MARGIN = 16;
const DEFAULT_CARD_WIDTH = 420;
const DEFAULT_CARD_HEIGHT = 280;
const MOBILE_MIN_CARD_HEIGHT = 140;
/** Keep tip cards compact beside the spotlight on phones. */
const MOBILE_CARD_VIEWPORT_CAP = 0.46;

export interface SaiQuickTourCardPosition {
    top: number;
    left: number;
}

export interface SaiQuickTourCardSize {
    width: number;
    height: number;
}

export interface SaiQuickTourSpotlightRect {
    x: number;
    y: number;
    width: number;
    height: number;
}

export interface SaiQuickTourLayoutInput {
    viewportWidth: number;
    viewportHeight: number;
    spotlight: SaiQuickTourSpotlightRect | null;
    placement: SaiQuickTourPlacement;
    cardSize: SaiQuickTourCardSize;
    margin?: number;
    /** Sidebar nav targets use a narrower card aligned to the drawer. */
    navTarget?: boolean;
}

export interface SaiQuickTourLayoutResult extends SaiQuickTourCardPosition {
    mobileDocked: boolean;
    /** Scrollable cap — card is anchored near the spotlight, not viewport-bottom. */
    maxHeight?: number;
    cardWidth?: number;
    /** Tip card is pinned beside the spotlight (all mobile targeted steps). */
    mobileAnchored?: boolean;
}

export function isSaiQuickTourMobileViewport(viewportWidth: number): boolean {
    return viewportWidth <= SAI_QUICK_TOUR_MOBILE_MAX_WIDTH;
}

export function isSaiQuickTourNavTarget(target: string | undefined): boolean {
    return !!target && /\[data-sai-tour="nav-/.test(target);
}

/** List-page primary CTA in `.sai-page-header__actions` (Add Data Source, Add Agent, …). */
export function isSaiQuickTourPageHeaderCtaTarget(target: string | undefined): boolean {
    return !!target && /\[data-sai-tour="add-(data-source|agent|division)"/.test(target);
}

/** Hierarchy tree actions that often sit below the fold on phones. */
export function isSaiQuickTourHierarchyActionTarget(target: string | undefined): boolean {
    return !!target && /\[data-sai-tour="add-(division|department)"/.test(target);
}

/**
 * Spotlight rects must hug the visible control, not a layout wrapper.
 * `.sai-page-header__actions-group` is full-width on mobile but only as tall as its row —
 * when the tour hook sits on the group, measure the primary button/link inside it.
 */
export function resolveTourMeasureElement(el: Element): Element {
    if (!(el instanceof HTMLElement)) {
        return el;
    }
    if (el.classList.contains('sai-page-header__actions-group')) {
        const control = el.querySelector<HTMLElement>(
            'button, a[mat-flat-button], a[mat-stroked-button], a[mat-button], a[mat-raised-button]',
        );
        if (control) {
            return control;
        }
    }
    return el;
}

/**
 * Prefer a truly interactive / on-screen target when several nodes share a tour id
 * (e.g. header CTA vs empty-state CTA, or one + button per division).
 * Falls back to an off-screen (but not hidden) match so sidebar rows that were scrolled
 * away can still be selected and then scrolled back into the drawer.
 */
export function findBestTourTargetElement(selector: string): Element | null {
    const matches = Array.from(document.querySelectorAll(selector));
    if (matches.length === 0) {
        return null;
    }

    let bestVisible: Element | null = null;
    let bestVisibleScore = -1;
    let bestAny: Element | null = null;
    let bestAnyScore = -1;

    for (const candidate of matches) {
        const measureEl = resolveTourMeasureElement(candidate);
        if (!isTourTargetCandidate(measureEl)) {
            continue;
        }

        const score = scoreTourTarget(measureEl, candidate);
        if (score > bestAnyScore) {
            bestAnyScore = score;
            bestAny = candidate;
        }

        if (isTourTargetVisible(measureEl) && score > bestVisibleScore) {
            bestVisibleScore = score;
            bestVisible = candidate;
        }
    }

    return bestVisible ?? bestAny;
}

/** Mobile scroll strategy — keep the target in the upper band so the tip can sit under it. */
export function resolveTourTargetScrollBlock(
    isMobile: boolean,
    target: string | undefined,
): ScrollLogicalPosition {
    if (!isMobile) {
        return 'nearest';
    }
    if (isSaiQuickTourNavTarget(target)) {
        // Nav scroll is handled specially in scrollTourTargetIntoView (sticky header).
        return 'nearest';
    }
    if (isSaiQuickTourPageHeaderCtaTarget(target) || isSaiQuickTourHierarchyActionTarget(target)) {
        return 'start';
    }
    return 'center';
}

/**
 * Scroll the measured target into a phone-friendly band so the tip card has room underneath.
 * Nav items scroll only inside the drawer content — never toward the viewport top, or they
 * slide under the sticky logo/profile header and the spotlight paints the wrong chrome.
 */
export function scrollTourTargetIntoView(
    el: Element,
    isMobile: boolean,
    target: string | undefined,
): void {
    const measureEl = resolveTourMeasureElement(el);

    if (isSaiQuickTourNavTarget(target)) {
        scrollNavTargetIntoDrawerView(measureEl);
        return;
    }

    if (!isMobile) {
        measureEl.scrollIntoView({ block: 'nearest', inline: 'nearest', behavior: 'auto' });
        return;
    }

    measureEl.scrollIntoView({
        block: resolveTourTargetScrollBlock(true, target),
        inline: 'nearest',
        behavior: 'auto',
    });

    const rect = measureEl.getBoundingClientRect();
    const desiredTop = Math.round(window.innerHeight * 0.18);
    const delta = rect.top - desiredTop;
    if (Math.abs(delta) < 16) {
        return;
    }

    const scroller = findScrollableAncestor(measureEl);
    if (scroller) {
        scroller.scrollTop += delta;
        return;
    }

    window.scrollBy(0, delta);
}

/** Keep a sidebar row fully inside the nav content scrollport (below the sticky header). */
function scrollNavTargetIntoDrawerView(measureEl: Element): void {
    const scroller = findScrollableAncestor(measureEl);
    if (!scroller) {
        measureEl.scrollIntoView({ block: 'nearest', inline: 'nearest', behavior: 'auto' });
        return;
    }

    const elRect = measureEl.getBoundingClientRect();
    const scrollerRect = scroller.getBoundingClientRect();
    const pad = 10;

    if (elRect.top < scrollerRect.top + pad) {
        scroller.scrollTop += elRect.top - scrollerRect.top - pad;
        return;
    }
    if (elRect.bottom > scrollerRect.bottom - pad) {
        scroller.scrollTop += elRect.bottom - scrollerRect.bottom + pad;
    }
}

/** Nav steps that also declare a route should not navigate on mobile until Next. */
export function shouldDeferNavStepRouteOnMobile(
    target: string | undefined,
    route: string | undefined,
    viewportWidth: number = typeof window !== 'undefined' ? window.innerWidth : SAI_QUICK_TOUR_MOBILE_MAX_WIDTH + 1,
): boolean {
    return !!route && isSaiQuickTourNavTarget(target) && isSaiQuickTourMobileViewport(viewportWidth);
}

/** Element exists, has size, and is not in a closed/hidden host — may still be scrolled off-screen. */
export function isTourTargetCandidate(el: Element): boolean {
    const rect = el.getBoundingClientRect();
    if (rect.width < 2 || rect.height < 2) {
        return false;
    }
    if (isInsideHiddenTourContext(el) || isInsideClosedOverlayNav(el)) {
        return false;
    }
    const style = window.getComputedStyle(el);
    if (style.display === 'none' || style.visibility === 'hidden' || Number(style.opacity) === 0) {
        return false;
    }
    return true;
}

export function isTourTargetVisible(el: Element): boolean {
    if (!isTourTargetCandidate(el)) {
        return false;
    }

    if (isObscuredByNavStickyHeader(el)) {
        return false;
    }

    const rect = el.getBoundingClientRect();
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    const visibleHeight = Math.min(rect.bottom, vh - 8) - Math.max(rect.top, 8);
    const visibleWidth = Math.min(rect.right, vw - 8) - Math.max(rect.left, 8);
    if (visibleHeight < 12 || visibleWidth < 12) {
        return false;
    }

    // Require a meaningful slice of the control — avoids stale / half-scrolled hits.
    return visibleHeight >= Math.min(24, rect.height * 0.45);
}

export function estimateSaiQuickTourCardSize(viewportWidth: number): SaiQuickTourCardSize {
    const margin = DEFAULT_MARGIN;
    const width = isSaiQuickTourMobileViewport(viewportWidth)
        ? Math.max(240, viewportWidth - margin * 2)
        : Math.min(DEFAULT_CARD_WIDTH, viewportWidth - margin * 2);
    const height = isSaiQuickTourMobileViewport(viewportWidth)
        ? DEFAULT_CARD_HEIGHT
        : DEFAULT_CARD_HEIGHT;
    return { width, height };
}

export function computeSaiQuickTourCardPosition(input: SaiQuickTourLayoutInput): SaiQuickTourLayoutResult {
    const margin = input.margin ?? DEFAULT_MARGIN;
    const { viewportWidth, viewportHeight, spotlight, cardSize } = input;
    const mobile = isSaiQuickTourMobileViewport(viewportWidth);

    if (!spotlight) {
        return {
            top: Math.max(margin, (viewportHeight - cardSize.height) / 2),
            left: Math.max(margin, (viewportWidth - cardSize.width) / 2),
            mobileDocked: false,
        };
    }

    if (mobile) {
        return computeAnchoredMobilePosition(
            spotlight,
            viewportWidth,
            viewportHeight,
            margin,
            !!input.navTarget,
        );
    }

    const placement = input.placement === 'center' ? 'bottom' : input.placement;
    let top = spotlight.y + spotlight.height + margin;
    let left = spotlight.x;

    if (placement === 'top') {
        top = spotlight.y - cardSize.height - margin;
    } else if (placement === 'right') {
        top = spotlight.y;
        left = spotlight.x + spotlight.width + margin;
    } else if (placement === 'left') {
        top = spotlight.y;
        left = spotlight.x - cardSize.width - margin;
    } else {
        left = spotlight.x + spotlight.width / 2 - cardSize.width / 2;
    }

    const resolved = clampCardPosition(
        { top, left },
        cardSize,
        viewportWidth,
        viewportHeight,
        margin,
    );

    const flipped = flipCardPositionIfNeeded(
        resolved,
        cardSize,
        spotlight,
        placement,
        viewportWidth,
        viewportHeight,
        margin,
    );

    return { ...flipped, mobileDocked: false };
}

/** Pin the card to the spotlight on mobile — below preferred, above fallback; never viewport-bottom dock. */
function computeAnchoredMobilePosition(
    spotlight: SaiQuickTourSpotlightRect,
    viewportWidth: number,
    viewportHeight: number,
    margin: number,
    navTarget: boolean,
): SaiQuickTourLayoutResult {
    const fullWidth = viewportWidth - margin * 2;
    const cardWidth = navTarget
        ? Math.min(fullWidth, Math.max(spotlight.width + margin, viewportWidth * 0.72))
        : fullWidth;
    const left = navTarget
        ? Math.max(margin, Math.min(spotlight.x, viewportWidth - cardWidth - margin))
        : Math.max(
            margin,
            Math.min(
                spotlight.x + spotlight.width / 2 - cardWidth / 2,
                viewportWidth - cardWidth - margin,
            ),
        );

    const viewportCap = Math.round(viewportHeight * MOBILE_CARD_VIEWPORT_CAP);
    const spaceBelow = viewportHeight - (spotlight.y + spotlight.height + margin) - margin;
    const spaceAbove = spotlight.y - margin * 2;

    if (spaceBelow >= MOBILE_MIN_CARD_HEIGHT || spaceBelow >= spaceAbove) {
        const maxHeight = Math.max(MOBILE_MIN_CARD_HEIGHT, Math.min(spaceBelow, viewportCap));
        return {
            top: spotlight.y + spotlight.height + margin,
            left,
            mobileDocked: false,
            mobileAnchored: true,
            maxHeight,
            cardWidth,
        };
    }

    // Sit directly above the spotlight (not parked at the viewport top).
    const maxHeight = Math.max(MOBILE_MIN_CARD_HEIGHT, Math.min(spaceAbove, viewportCap));
    const top = Math.max(margin, spotlight.y - maxHeight - margin);
    return {
        top,
        left,
        mobileDocked: false,
        mobileAnchored: true,
        maxHeight,
        cardWidth,
    };
}

function scoreTourTarget(measureEl: Element, hookEl: Element): number {
    const rect = measureEl.getBoundingClientRect();
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    const visibleHeight = Math.min(rect.bottom, vh) - Math.max(rect.top, 0);
    const visibleWidth = Math.min(rect.right, vw) - Math.max(rect.left, 0);
    const area = Math.max(0, visibleHeight) * Math.max(0, visibleWidth);

    let score = area;
    // Prefer persistent page-header CTAs over empty-state duplicates.
    if (hookEl.closest('.sai-page-header, .dept-hierarchy__card > .flex')) {
        score += 50_000;
    }
    // Prefer real controls over wrappers.
    if (measureEl instanceof HTMLButtonElement || measureEl.getAttribute('mat-flat-button') !== null) {
        score += 10_000;
    }
    // Prefer targets closer to the upper third (where tips usually sit below).
    const centerY = rect.top + rect.height / 2;
    score -= Math.abs(centerY - vh * 0.28) * 4;
    return score;
}

function isInsideHiddenTourContext(el: Element): boolean {
    let node: Element | null = el;
    while (node && node !== document.documentElement) {
        if (node.hasAttribute('hidden')) {
            return true;
        }
        if (node.getAttribute('aria-hidden') === 'true') {
            return true;
        }
        // Inactive Material tabs stay in the DOM; never spotlight their descendants.
        if (
            node.classList.contains('mat-mdc-tab-body') &&
            !node.classList.contains('mat-mdc-tab-body-active')
        ) {
            return true;
        }
        node = node.parentElement;
    }
    return false;
}

/** Closed Fuse overlay drawer still keeps items in the DOM with layout boxes. */
function isInsideClosedOverlayNav(el: Element): boolean {
    const nav = el.closest('fuse-vertical-navigation');
    if (!nav) {
        return false;
    }
    return (
        nav.classList.contains('fuse-vertical-navigation-mode-over') &&
        !nav.classList.contains('fuse-vertical-navigation-opened')
    );
}

/**
 * Nav content sits under a sticky logo/profile header. A row scrolled "under" that
 * header still has a viewport rect — reject it so the spotlight cannot paint the logo.
 */
function isObscuredByNavStickyHeader(el: Element): boolean {
    const scroller = el.closest('.fuse-vertical-navigation-content');
    if (!scroller) {
        return false;
    }
    const elRect = el.getBoundingClientRect();
    const scrollerRect = scroller.getBoundingClientRect();
    return elRect.bottom <= scrollerRect.top + 4;
}

function findScrollableAncestor(el: Element): HTMLElement | null {
    let node: HTMLElement | null = el instanceof HTMLElement ? el.parentElement : null;
    while (node && node !== document.body) {
        const style = window.getComputedStyle(node);
        const overflowY = style.overflowY;
        const canScroll =
            (overflowY === 'auto' || overflowY === 'scroll' || overflowY === 'overlay') &&
            node.scrollHeight > node.clientHeight + 4;
        if (canScroll) {
            return node;
        }
        node = node.parentElement;
    }
    return null;
}

function clampCardPosition(
    position: SaiQuickTourCardPosition,
    cardSize: SaiQuickTourCardSize,
    viewportWidth: number,
    viewportHeight: number,
    margin: number,
): SaiQuickTourCardPosition {
    return {
        top: Math.max(margin, Math.min(position.top, viewportHeight - cardSize.height - margin)),
        left: Math.max(margin, Math.min(position.left, viewportWidth - cardSize.width - margin)),
    };
}

function flipCardPositionIfNeeded(
    position: SaiQuickTourCardPosition,
    cardSize: SaiQuickTourCardSize,
    spotlight: SaiQuickTourSpotlightRect,
    placement: SaiQuickTourPlacement,
    viewportWidth: number,
    viewportHeight: number,
    margin: number,
): SaiQuickTourCardPosition {
    const overlapsSpotlight = rectsOverlap(position, cardSize, spotlight, margin);
    if (!overlapsSpotlight) {
        return position;
    }

    const candidates: SaiQuickTourPlacement[] = [];
    if (placement === 'bottom') {
        candidates.push('top', 'right', 'left');
    } else if (placement === 'top') {
        candidates.push('bottom', 'right', 'left');
    } else if (placement === 'right') {
        candidates.push('left', 'bottom', 'top');
    } else if (placement === 'left') {
        candidates.push('right', 'bottom', 'top');
    } else {
        candidates.push('bottom', 'top');
    }

    for (const candidate of candidates) {
        const next = computePlacementPosition(candidate, spotlight, cardSize, margin);
        const clamped = clampCardPosition(next, cardSize, viewportWidth, viewportHeight, margin);
        if (!rectsOverlap(clamped, cardSize, spotlight, margin)) {
            return clamped;
        }
    }

    return position;
}

function computePlacementPosition(
    placement: SaiQuickTourPlacement,
    spotlight: SaiQuickTourSpotlightRect,
    cardSize: SaiQuickTourCardSize,
    margin: number,
): SaiQuickTourCardPosition {
    if (placement === 'top') {
        return { top: spotlight.y - cardSize.height - margin, left: spotlight.x };
    }
    if (placement === 'right') {
        return { top: spotlight.y, left: spotlight.x + spotlight.width + margin };
    }
    if (placement === 'left') {
        return { top: spotlight.y, left: spotlight.x - cardSize.width - margin };
    }
    return {
        top: spotlight.y + spotlight.height + margin,
        left: spotlight.x + spotlight.width / 2 - cardSize.width / 2,
    };
}

function rectsOverlap(
    position: SaiQuickTourCardPosition,
    cardSize: SaiQuickTourCardSize,
    spotlight: SaiQuickTourSpotlightRect,
    margin: number,
): boolean {
    const cardTop = position.top - margin;
    const cardLeft = position.left - margin;
    const cardBottom = position.top + cardSize.height + margin;
    const cardRight = position.left + cardSize.width + margin;
    const spotBottom = spotlight.y + spotlight.height;
    const spotRight = spotlight.x + spotlight.width;

    return !(
        cardBottom < spotlight.y ||
        cardTop > spotBottom ||
        cardRight < spotlight.x ||
        cardLeft > spotRight
    );
}
